import React, { createContext, useReducer, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config';

const initialState = {
  formData: {
    ad: "",
    soyad: "",
    email: "",
    telefon: "",
    sifre: "",
    adres: "",
  },
  loading: true,
  referralOrders: [],
  totalCommission: 0,
  coupons: []
};

const userReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_USER_DATA':
      return {
        ...state,
        formData: {
          ...state.formData,
          ...action.payload,
          sifre: ""
        },
        loading: false
      };
    case 'UPDATE_USER_DATA':
      return { ...state, formData: { ...state.formData, ...action.payload } };
    case 'SET_REFERRAL_ORDERS':
      return {
        ...state,
        referralOrders: action.payload.orders,
        totalCommission: action.payload.commission
      };
    case 'SET_COUPONS':
      return {
        ...state,
        coupons: action.payload
      };
    case 'CLEAR_USER_DATA':
      return initialState;
    default:
      return state;
  }
};

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(userReducer, initialState);

  const fetchUserData = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true });
    dispatch({ type: 'CLEAR_USER_DATA' });

    const token = localStorage.getItem('token');
    if (!token) {
      dispatch({ type: 'SET_LOADING', payload: false });
      return;
    }

    try {
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/user`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        },
      });

      if (!response.ok) {
        throw new Error('Kullanıcı bilgileri alınamadı');
      }

      const userData = await response.json();
      dispatch({ type: 'SET_USER_DATA', payload: userData });
    } catch (error) {
      console.error("Kullanıcı bilgileri yüklenemedi:", error);
      dispatch({ type: 'CLEAR_USER_DATA' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: 'UPDATE_USER_DATA', payload: { [name]: value } });
  };

  const handleSubmit = async (e, onSuccess, onError) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      let dataToSend = { ...state.formData };

      if (!dataToSend.sifre || dataToSend.sifre.trim() === '') {
        const { sifre, ...dataWithoutPassword } = dataToSend;
        dataToSend = dataWithoutPassword;
      }

      const response = await axios.put(`${getApiUrl()}/user`, dataToSend, {
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        dispatch({ type: 'SET_USER_DATA', payload: response.data });
        onSuccess?.("Bilgiler başarıyla güncellendi.");

        if (state.formData.sifre && state.formData.sifre.trim() !== '') {
          setTimeout(() => {
            logout();
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Bilgiler güncellenemedi:", error);
      if (error.response?.status === 400) {
        onError?.("Güncelleme hatası: Adres bilgisi eksik veya hatalı. Lütfen adres ekleyiniz.");
      } else {
        onError?.("Bilgiler güncellenemedi.");
      }
    }
  };

  const changePassword = async (oldPassword, newPassword, onSuccess, onError) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      const response = await axios.put(`${getApiUrl()}/user`, {
        ...state.formData,
        sifre: newPassword,
        oldPassword: oldPassword
      }, {
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data) {
        onSuccess?.("Şifreniz başarıyla güncellendi.");
        setTimeout(() => {
          logout();
        }, 1000);
      }
    } catch (error) {
      console.error("Şifre güncellenemedi:", error);
      if (error.response?.status === 400) {
        onError?.("Mevcut şifreniz hatalı.");
      } else {
        onError?.("Şifre güncellenirken bir hata oluştu.");
      }
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tkn');
    dispatch({ type: 'CLEAR_USER_DATA' });
    window.location.href = "/login";
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${getApiUrl()}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        throw new Error('Giriş başarısız');
      }

      const data = await response.json();

      // API yanıtını kontrol et
      if (data.success) {
        localStorage.setItem('token', data.success);
        if (data.tkn) {
          localStorage.setItem('tkn', data.tkn);
        }

        // Giriş başarılı olduktan sonra kullanıcı bilgilerini getir
        await fetchUserData();
        return true;
      } else {
        throw new Error(data.message || 'Giriş başarısız');
      }
    } catch (error) {
      console.error("Giriş hatası:", error);
      throw error;
    }
  };

  const getReferralOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      if (!token || !tkn) {
        dispatch({ type: 'SET_REFERRAL_ORDERS', payload: { orders: [], commission: 0 } });
        return { orders: [], commission: 0 };
      }

      const response = await fetch(`${getApiUrl()}/referral-orders`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        }
      });

      if (!response.ok) {
        console.error('API yanıtı başarısız:', response.status);
        dispatch({ type: 'SET_REFERRAL_ORDERS', payload: { orders: [], commission: 0 } });
        return { orders: [], commission: 0 };
      }
      const data = await response.json();

      // Veri kontrolü ve güvenli dönüşüm
      if (!data || !data.codes || typeof data.codes !== 'object') {
        dispatch({ type: 'SET_REFERRAL_ORDERS', payload: { orders: [], commission: 0 } });
        return { orders: [], commission: 0 };
      }

      const formattedOrders = Object.entries(data.codes).map(([refCode, refData]) => {
        if (!refData || !refData.products || !Array.isArray(refData.products)) {
          return {
            id: refCode,
            referral_code: refCode,
            products: []
          };
        }

        return {
          id: refCode,
          referral_code: refCode,
          products: refData.products.map(product => ({
            product_image: product?.image || '',
            product_title: product?.title || 'Ürün Adı Yok',
            total_amount: product?.price || 0
          }))
        };
      });

      dispatch({
        type: 'SET_REFERRAL_ORDERS',
        payload: {
          orders: formattedOrders,
          commission: data.komisyon || 0
        }
      });
      return { orders: formattedOrders, commission: data.komisyon || 0 };
    } catch (error) {
      console.error("Referans siparişleri yüklenirken hata oluştu:", error);
      dispatch({ type: 'SET_REFERRAL_ORDERS', payload: { orders: [], commission: 0 } });
      return { orders: [], commission: 0 };
    }
  }, [dispatch]);

  // const createCoupon = async (amount) => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     const tkn = localStorage.getItem('tkn');

  //     function generateRandomCode(prefix = 'CPN', length = 8) {
  //       const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

  //       const timestamp = Date.now().toString(36);
  //       const randomPart = Array.from(
  //         window.crypto.getRandomValues(new Uint8Array(length))
  //       )
  //         .map(byte => characters[byte % characters.length])
  //         .join('');

  //       const code = `${prefix}-${timestamp.slice(-4)}${randomPart.slice(0, length - 4)}`;

  //       return code.toUpperCase();
  //     }

  //     const couponCode = generateRandomCode();

  //     const response = await fetch("https://api.bikuyum.com/api/coupons", {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `${token}`,
  //         'Tkn': `${tkn}`,
  //       },
  //       body: JSON.stringify({ 
  //         coupon_code: couponCode,
  //         amount: amount,
  //         type: "unlimited"
  //       })
  //     });

  //     const data = await response.json();

  //     if (data.success) {
  //       return {
  //         success: true,
  //         couponCode: data.couponCode || couponCode,
  //         message: "Kupon başarıyla oluşturuldu"
  //       };
  //     } else {
  //       throw new Error(data.message || "Kupon oluşturulamadı");
  //     }
  //   } catch (error) {
  //     console.error("Kupon oluşturma hatası:", error);
  //     throw error;
  //   }
  // };

  const createReferralCode = async (customCode = null) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      function generateRandomCode(length = 8) {
        const characters = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

        const timestamp = Date.now().toString(36);
        const randomPart = Array.from(
          window.crypto.getRandomValues(new Uint8Array(length))
        )
          .map(byte => characters[byte % characters.length])
          .join('');

        const code = `${timestamp.slice(-4)}${randomPart.slice(0, length - 4)}`;

        return code.toUpperCase();
      }

      let share_code;
      
      if (customCode) {
        share_code = customCode.toUpperCase();
      } else {
        share_code = generateRandomCode();
      }

      const response = await fetch("https://api.bikuyum.com/api/cart/share", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        },
        body: JSON.stringify({ 
          share_code: share_code,
          type: "unlimited"
        })
      });

      const data = await response.json();

      if (data.success) {
        return {
          success: true,
          share_code: data.share_code || share_code,
          message: "Referans kodu başarıyla oluşturuldu"
        };
      } else if (data.share_code) {
        return {
          success: true,
          share_code: data.share_code,
          type: data.type,
          message: "Referans kodu başarıyla oluşturuldu"
        };
      } else {
        throw new Error(data.message || "Referans kodu oluşturulamadı");
      }
    } catch (error) {
      console.error("Referans kodu oluşturma hatası:", error);
      throw error;
    }
  };

  // const getCoupons = useCallback(async () => {
  //   try {
  //     const token = localStorage.getItem('token');
  //     const tkn = localStorage.getItem('tkn');

  //     if (!token || !tkn) {
  //       dispatch({ type: 'SET_COUPONS', payload: [] });
  //       return [];
  //     }

  //     const response = await fetch("https://api.bikuyum.com/api/coupons", {
  //       method: 'GET',
  //       headers: {
  //         'Content-Type': 'application/json',
  //         'Authorization': `${token}`,
  //         'Tkn': `${tkn}`,
  //       }
  //     });

  //     if (!response.ok) {
  //       console.error('API yanıtı başarısız:', response.status);
  //       dispatch({ type: 'SET_COUPONS', payload: [] });
  //       return [];
  //     }

  //     const data = await response.json();
  //     dispatch({ type: 'SET_COUPONS', payload: data });
  //     return data;
  //   } catch (error) {
  //     console.error("Kuponlar yüklenirken hata oluştu:", error);
  //     dispatch({ type: 'SET_COUPONS', payload: [] });
  //     return [];
  //   }
  // }, [dispatch]);

  const getAddresses = async () => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/addresses`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Adresler yüklenemedi:", error);
      throw error;
    }
  };

  const addAddress = async (addressData) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/addresses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        },
        body: JSON.stringify(addressData)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Adres eklenemedi:", error);
      throw error;
    }
  };

  const updateAddress = async (addressId, addressData) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/addresses/${addressId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        },
        body: JSON.stringify(addressData)
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Adres güncellenemedi:", error);
      throw error;
    }
  };

  const deleteAddress = async (addressId) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/${addressId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Adres silinemedi:", error);
      throw error;
    }
  };

  return (
    <UserContext.Provider value={{
      ...state,
      handleInputChange,
      handleSubmit,
      logout,
      login,
      fetchUserData,
      getReferralOrders,
      referralOrders: state.referralOrders,
      totalCommission: state.totalCommission,
      changePassword,
      // createCoupon,
      createReferralCode,
      // getCoupons,
      // coupons: state.coupons,
      getAddresses,
      addAddress,
      updateAddress,
      deleteAddress
    }}>
      {children}
    </UserContext.Provider>
  );
};